// Lion Auto holding (built for General Admission groups, where the tickets
// are interchangeable): a group's criteria.holdLive = X (0 = off) splits its
// ranked members in two tiers. Ranks 1..X are LIVE: the ladder prices them as
// always. The Live qty, criteria.holdSeats = S (0 = off), asks for at least
// S seats live at the get-in: while ranks 1..X hold fewer seats (each
// listing's quantity), the next ranks go live too, one by one, until they
// hold S (whichever needs more ranks; with fewer seats in the whole group
// every rank is live). Later ranks are HELD at one hold price the pricer
// types (criteria.holdPrice, a B; never under a rank's floor), with no
// what-if. When a live listing sells the ranks close up, so the first held
// one goes live and the ladder prices it down. The server prices both tiers
// and tags each rung (rung.tier 'LIVE' | 'HELD', null with holding off), and
// says how many ranks and seats went live (the answer's hold.liveRanks /
// liveSeats); these helpers clean the three settings as the server bounds
// them, count and number the tiers the way the server will, and word them.
// Pure; tested in lionAutoHold.test.js.
import { MAX_LADDER, cleanLadderAmount, floorHeldText } from './lionAutoLadder';

// The server's cap for the hold price (B dollars, cents allowed): the
// ladder's highest B
export const MAX_HOLD_PRICE = 100000;
// The server's cap for the Live qty (whole seats)
export const MAX_HOLD_SEATS = 5000;

// The tag next to a rank, in words (a tooltip)
export const TIER_TITLES = Object.freeze({
  LIVE: 'Live: the ladder prices it',
  HELD: 'Held at the hold price (B) until a live listing sells; then the ranks close up and the first held one goes live',
});

// A held rung's New L cell: no what-if was asked for it
export const HELD_L_TITLE = "Held at the hold price (B), so no what-if was asked: Skybox gives its L once the price is written";
// The same for a held rung its floor beat the hold price
export const FLOOR_HELD_L_TITLE = "Held at its floor (B), above the hold price, so no what-if was asked: Skybox gives its L once the price is written";

const usd = (v) => `$${Number(v).toFixed(2)}`;
const cents = (v) => Math.round(Number(v) * 100);
const seatsText = (n) => `${n} seat${n === 1 ? '' : 's'}`;

// A whole number 0..max, from a number or a box's text; anything else = 0
function wholeUpTo(value, max) {
  let n = NaN;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : 0;
}

// One listing's seats: its quantity; null, negative, fractional or
// non-finite = 0
const seatsOf = (quantity) => wholeUpTo(quantity, Number.MAX_SAFE_INTEGER);

/** Live listings: a whole number 0..MAX_LADDER (500); junk, fractions and out of range = 0 (holding off) */
export function cleanHoldLive(value) {
  return wholeUpTo(value, MAX_LADDER);
}

/** Live qty: a whole number of seats 0..MAX_HOLD_SEATS (5000); junk, fractions and out of range = 0 (off) */
export function cleanHoldSeats(value) {
  return wholeUpTo(value, MAX_HOLD_SEATS);
}

/** The hold price: 0..MAX_HOLD_PRICE B dollars rounded to the cent; junk or out of range = 0 */
export function cleanHoldPrice(value) {
  return cleanLadderAmount(value, 0, MAX_HOLD_PRICE);
}

/** Holding is on: a live count above 0 */
export function holdOn(criteria) {
  return cleanHoldLive(criteria?.holdLive) > 0;
}

/** Holding is on with no hold price (the server refuses that pair) */
export function holdNeedsPrice(criteria) {
  return holdOn(criteria) && cents(cleanHoldPrice(criteria?.holdPrice)) <= 0;
}

/**
 * The cleaned hold settings, as the ladder answer's `hold` reads (before an
 * answer: it has no liveRanks / liveSeats yet)
 * @returns {{ live: number, seats: number, price: number }|null}  null with holding off
 */
export function holdOf(criteria) {
  if (!holdOn(criteria)) return null;
  return {
    live: cleanHoldLive(criteria.holdLive),
    seats: cleanHoldSeats(criteria.holdSeats),
    price: cleanHoldPrice(criteria.holdPrice),
  };
}

/**
 * How many ranks are LIVE, as the server counts them: the fewest ranks k
 * with k >= holdLive and seats(ranks 1..k) >= holdSeats (whichever needs
 * more); every rank when the whole group holds fewer seats. A listing's
 * seats are its quantity (null, negative, fractional or non-finite = 0).
 * Holding off (holdLive 0): 0, so holdTierOf gives no rank a tier.
 * @param {*} holdLive     the criteria's live count
 * @param {*} holdSeats    the criteria's Live qty (0 = off)
 * @param {Array<*>} quantities  the ranked members' quantities, rank 1 first (quantitiesByRank)
 * @returns {number}  0..quantities.length
 */
export function liveRanksOf(holdLive, holdSeats, quantities) {
  const live = cleanHoldLive(holdLive);
  if (live === 0) return 0;
  const list = Array.isArray(quantities) ? quantities : [];
  const wanted = cleanHoldSeats(holdSeats);
  let n = Math.min(live, list.length);
  let seats = 0;
  for (let i = 0; i < n; i += 1) seats += seatsOf(list[i]);
  while (seats < wanted && n < list.length) {
    seats += seatsOf(list[n]);
    n += 1;
  }
  return n;
}

/**
 * The ranked members' quantities in rank order, for liveRanksOf: index
 * rank - 1, null for a ranked id not among the listings (its seats count 0)
 * @param {Map<string, number>} rankById  inventory id string → rank 1..n (lionAutoLadder ladderRanks)
 * @param {Array<{id, quantity}>} listings  our listings, any order (all of them, not a filtered view)
 * @returns {Array<*>}
 */
export function quantitiesByRank(rankById, listings) {
  if (!(rankById instanceof Map) || rankById.size === 0) return [];
  const out = new Array(rankById.size).fill(null);
  for (const l of Array.isArray(listings) ? listings : []) {
    if (l?.id === null || l?.id === undefined) continue;
    const rank = rankById.get(String(l.id));
    if (Number.isInteger(rank) && rank >= 1 && rank <= out.length) out[rank - 1] = l.quantity ?? null;
  }
  return out;
}

/**
 * Where the ranks' quantities come from, as { id, quantity } rows for
 * quantitiesByRank: once the group's match result is in, its members'
 * listings as the server read them (the same read the ranks come from, so a
 * part sale counts as soon as the match sees it; a member with no listing
 * counts 0); our listings (pricing.myInventory, all of them) only while
 * there is no result
 * @param {object|null} result  the group's match result (members: [{ inventoryId, listing }])
 * @param {Array<{id, quantity}>|null} myInventory  our listings
 * @returns {Array<{id, quantity}>}
 */
export function seatRowsOf(result, myInventory) {
  if (!result) return Array.isArray(myInventory) ? myInventory : [];
  return (Array.isArray(result.members) ? result.members : [])
    .map(m => ({ id: m?.inventoryId ?? null, quantity: m?.listing?.quantity ?? null }));
}

/**
 * The tier a rank gets: 1..liveCount LIVE, later ones HELD
 * @param {*} rank       the server's rank (1 = the lowest price); none for an unranked listing
 * @param {*} liveCount  how many ranks are live (liveRanksOf; with the Live qty off it is the
 *                       criteria's live count, capped at the ranks there are)
 * @returns {'LIVE'|'HELD'|null}  null with holding off (0) or no rank
 */
export function holdTierOf(rank, liveCount) {
  const live = cleanHoldLive(liveCount);
  const r = Number(rank);
  if (live === 0 || rank === null || rank === undefined || !Number.isInteger(r) || r < 1) return null;
  return r <= live ? 'LIVE' : 'HELD';
}

/**
 * A warning (never a block) when the hold price is not above the highest new
 * B of the priced LIVE rungs, compared in whole cents: the held listings would
 * then undercut the live ones
 * @param {object[]} rungs  the ladder answer's rungs
 * @param {*} holdPrice     the hold price (B)
 * @returns {string|null}
 */
export function holdWarning(rungs, holdPrice) {
  const price = cleanHoldPrice(holdPrice);
  if (cents(price) <= 0) return null;
  let top = null;
  for (const r of Array.isArray(rungs) ? rungs : []) {
    if (r?.tier !== 'LIVE' || (r.status !== 'OK' && r.status !== 'UNCHANGED')) continue;
    if (r.newB === null || r.newB === undefined || r.newB === '' || !Number.isFinite(Number(r.newB))) continue;
    if (top === null || cents(r.newB) > cents(top)) top = Number(r.newB);
  }
  if (top === null || cents(price) > cents(top)) return null;
  return `Hold price ${usd(price)} is not above the highest live price (${usd(cents(top) / 100)} B): `
    + 'the held listings would undercut the live ones. Raise the hold price.';
}

/** The group card chip: "Hold · 4 live · $500.00", "Hold · 4 live · 15 seats · $500.00" with a Live qty; null with holding off */
export function holdChipText(criteria) {
  const hold = holdOf(criteria);
  if (!hold) return null;
  const seats = hold.seats > 0 ? ` · ${seatsText(hold.seats)}` : '';
  return `Hold · ${hold.live} live${seats} · ${hold.price > 0 ? usd(hold.price) : 'no price'}`;
}

/**
 * What holding does, for a live count and a Live qty (the editor's hint, the
 * chip's title); '' with holding off. The Live qty off: the live count's words.
 * With it, a sale is said in seats: later ranks go live until it is met again.
 */
export function holdHintText(holdLive, holdSeats = 0) {
  const live = cleanHoldLive(holdLive);
  if (live === 0) return '';
  const seats = cleanHoldSeats(holdSeats);
  const head = live === 1 ? 'Rank 1 gets the ladder price' : `Ranks 1–${live} get the ladder price`;
  if (seats > 0) {
    return `${head}, plus later ranks until ${seatsText(seats)} ${seats === 1 ? 'is' : 'are'} at the get-in; `
      + 'the rest sit at the hold price (B). When live seats sell, later ranks go live until the Live qty is met again.';
  }
  if (live === 1) return `${head}; later ranks sit at the hold price (B). When it sells, the next moves down.`;
  return `${head}; later ranks sit at the hold price (B). When a live one sells, the next moves down.`;
}

/**
 * The ladder bar's head line: "ranks 5+ held at $500.00 (B)"; null with
 * holding off. With a Live qty: from an answer (its liveRanks / liveSeats)
 * "ranks 3+ held at $500.00 (B) · 16 seats live", or "nothing held · 12
 * seats live" when every rank went live (the group holds fewer seats than
 * the Live qty, or the answer has no HELD rung); before an answer (no
 * liveRanks yet) "held at $500.00 (B) after 4 ranks and 15+ seats live". The
 * Live qty off (or an older server's answer, { live, price }): the live
 * count's words, as before.
 * @param {object|null} hold   the answer's hold, else the criteria's (holdOf)
 * @param {object[]|null} [rungs]  the answer's rungs (the HELD ones come back on every answer)
 * @returns {string|null}
 */
export function holdHeadText(hold, rungs = null) {
  const live = cleanHoldLive(hold?.live);
  if (live === 0) return null;
  const price = cleanHoldPrice(hold.price);
  const seats = cleanHoldSeats(hold.seats);
  const heldFrom = (rank) => (price > 0 ? `ranks ${rank}+ held at ${usd(price)} (B)` : `ranks ${rank}+ held, no hold price yet`);
  if (seats === 0) return heldFrom(live + 1);
  if (hold.liveRanks === null || hold.liveRanks === undefined) {
    // "15+ seats"; one seat is any seat, so just "1 seat"
    const after = `after ${live} rank${live === 1 ? '' : 's'} and ${seats === 1 ? '1 seat' : `${seats}+ seats`} live`;
    return price > 0 ? `held at ${usd(price)} (B) ${after}` : `held ${after}, no hold price yet`;
  }
  const liveRanks = wholeUpTo(hold.liveRanks, MAX_LADDER);
  const liveSeats = seatsOf(hold.liveSeats);
  const seatsLive = `${seatsText(liveSeats)} live`;
  const noneHeld = liveSeats < seats || (Array.isArray(rungs) && !rungs.some(r => r?.tier === 'HELD'));
  return noneHeld ? `nothing held · ${seatsLive}` : `${heldFrom(liveRanks + 1)} · ${seatsLive}`;
}

/** A held rung's New L cell: "Hold $500.00" */
export function heldLText(holdPrice) {
  const price = cleanHoldPrice(holdPrice);
  return price > 0 ? `Hold ${usd(price)}` : 'Hold';
}

/**
 * A held rung's New L cell and its tooltip, saying the B it is written at
 * (no what-if was asked for it): "Hold $500.00" at the hold price, "Floor
 * $520.00" (its own new B) when its floor beat the hold price, so the cell
 * never names a price other than its New B
 * @param {object} rung     a HELD rung of the ladder answer
 * @param {*} holdPrice     the answer's hold price (B)
 * @returns {{ text: string, title: string }}
 */
export function heldLCell(rung, holdPrice) {
  if (rung?.floored !== true) return { text: heldLText(holdPrice), title: HELD_L_TITLE };
  const b = rung.newB;
  const hasB = b !== null && b !== undefined && b !== '' && Number.isFinite(Number(b));
  return { text: hasB ? `Floor ${usd(b)}` : 'Floor', title: FLOOR_HELD_L_TITLE };
}

/** A held rung whose floor beat the hold price, for a tooltip (the floor B only when the server sent it) */
export function floorOverHoldText(rung, holdPrice, floorPct) {
  const price = cleanHoldPrice(holdPrice);
  return `${floorHeldText(rung, floorPct)}, above the hold price${price > 0 ? ` ${usd(price)}` : ''}`;
}
